import * as Yup from 'yup';
import styled from 'styled-components';
import { rgba } from 'polished';
import { Formik, Form } from 'formik';
import { useState } from 'react';
import { Trans } from 'react-i18next';
import { useRouter } from 'next/router';

import { t } from 'localization/utils/t';
import { bugsnag } from 'shared/utils/services/bugsnag';
import { Button } from 'shared/components/Button';
import { FormikInput } from 'shared/components/FormikInput';
import { Input } from 'shared/components/Input';
import { sharedValidationsMap } from 'shared/utils/validations';
import { variableFontMedium, variableFontRegular } from 'styles/theme/text';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { toggleVisible } from 'styles/theme/animation';
import { useOnRouteChangeComplete } from 'shared/hooks/useOnRouteChangeComplete';
import { useOutsideClick } from 'shared/hooks/useOutsideClick';
import { Link } from 'shared/components/Link';
import { LOCALES } from 'localization/constants/configEnums';
import { registerEmailNewsletter } from '../services/registerEmailNewsletter';

const TP = 'layout.footer.components.EmailNewsletter';

const initialValues = {
  emailNewsletter: '',
};

export const EmailNewsletter = ({
  setIsBuyBarVisible = null,
}: {
  setIsBuyBarVisible?: (val: boolean) => void;
}) => {
  const [isDisclaimerVisible, setIsDisclaimerVisible] = useState(false);
  const [emailSentSuccessMessage, setEmailSentSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);
  const router = useRouter();

  const emailValidationSchema = Yup.object().shape({
    emailNewsletter: sharedValidationsMap.emailValidation(),
  });

  const resetForm = () => setEmailSentSuccessMessage('');
  useOnRouteChangeComplete(resetForm);

  const hideDisclaimer = () => {
    setIsDisclaimerVisible(false);
    if (setIsBuyBarVisible) {
      setIsBuyBarVisible(true);
    }
  };
  const newsletterRef = useOutsideClick(hideDisclaimer);

  const handleFocus = () => {
    setIsDisclaimerVisible(true);
    if (setIsBuyBarVisible) {
      setIsBuyBarVisible(false);
    }
  };

  const handleSubmit = async (values, actions) => {
    try {
      const response = await registerEmailNewsletter(values.emailNewsletter);
      if (response?.message) {
        actions.resetForm({ values: initialValues });
        setEmailSentSuccessMessage(t(`${TP}.successMessage`, 'Thanks for signing up.'));
        sendTrackingEvent('FOOTER_NEWSLETTER_EMAIL_SIGN_UP_TAP');
      }
    } catch (error) {
      setErrorMessage(error.message);
      sendTrackingEvent('FOOTER_NEWSLETTER_EMAIL_SIGN_UP_TAP', {
        error_message: error.message,
      });
      bugsnag(new Error(error), { context: 'EmailNewsletter' });
    }
  };

  if (emailSentSuccessMessage) {
    return (
      <EmailNewsletter.SuccessMessage data-qa="email_newsletter_success_message">
        {emailSentSuccessMessage}
      </EmailNewsletter.SuccessMessage>
    );
  }

  return (
    <EmailNewsletter.Wrapper ref={newsletterRef}>
      <p>{t(`${TP}.label`, 'Sign up for exclusive promos, popular releases & more')}</p>
      <Formik
        initialValues={initialValues}
        onSubmit={(values, actions) => handleSubmit(values, actions)}
        validationSchema={emailValidationSchema}
      >
        {({ isSubmitting }) => {
          return (
            <Form autoComplete="off">
              <EmailNewsletter.Input hasLineBreak={router?.locale === LOCALES.frFR}>
                <FormikInput
                  data-qa="email_newsletter"
                  errorMessage={errorMessage}
                  hasError={!!errorMessage}
                  hasFloatingLabel={false}
                  placeholder={t(`${TP}.emailAddress`, 'Email Address')}
                  name="emailNewsletter"
                  onFocus={handleFocus}
                  type="email"
                />
              </EmailNewsletter.Input>
              <EmailNewsletter.SubmitButton
                data-qa="email_newsletter_submit"
                disabled={isSubmitting}
                type="submit"
                title={t(`${TP}.signUp`, 'Sign Up')}
              />
              <EmailNewsletter.Disclaimer
                data-qa="email_newsletter_disclaimer"
                isVisible={isDisclaimerVisible}
              >
                <Trans
                  i18nKey={`${TP}.emailNewsletterDisclaimer`}
                  defaults="<0>By signing up, you confirm you want to receive emails about GOAT's products and services and you agree to our </0><1>Terms & Conditions</1><0> and </0><2>Privacy Policy</2>."
                  components={[
                    <span />,
                    <Link href="/terms" target="_blank" passHref />,
                    <Link href="/privacy" target="_blank" passHref />,
                  ]}
                />
              </EmailNewsletter.Disclaimer>
            </Form>
          );
        }}
      </Formik>
    </EmailNewsletter.Wrapper>
  );
};

EmailNewsletter.Input = styled.div<{ hasLineBreak?: boolean }>`
  ${Input.Input} {
    &:focus + label {
      bottom: 4px;
    }

    border: none;
    padding: 18px 16px 16px;

    ::placeholder {
      ${variableFontRegular({ size: '11px', height: '14px' })};
      color: ${({ theme }) => theme.navigation.textColor};
    }

    html[lang='fr-fr'] & + label {
      white-space: normal;
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
      ::placeholder {
        ${variableFontRegular({ size: '12.5px', height: '12.5px' })};
      }
    }
  }

  ${Input.Error} {
    margin-bottom: 5px;
  }
`;

EmailNewsletter.SuccessMessage = styled.div`
  margin: 20px 16px;
  white-space: nowrap;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    margin: 0;
  }
`;

EmailNewsletter.Wrapper = styled.div`
  position: relative;
  margin: 20px 16px;

  ${Input.Input} {
    ${variableFontRegular({ size: '12px', height: '14px' })};
    padding-right: 45px;
  }

  ${Input.Label} {
    color: ${({ theme }) => theme.navigation.textColor};
  }

  ${Input.Content} {
    margin-bottom: 0;
    margin-top: 0;
  }

  ${Input.Wrapper} {
    border: 1px solid ${({ theme }) => rgba(theme.navigation.borderColor, 0.25)};
    border-radius: ${({ theme }) => theme.borderRadii.button};
  }

  p {
    ${variableFontMedium({ size: '12.5px', height: '12.5px' })};
    margin-bottom: 24px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    margin: 0;
    max-width: 400px;
    min-width: 358px;
    width: 100%;

    p {
      margin-bottom: 16px;
    }

    ${Input.Input} {
      ${variableFontRegular({ size: '12.5px', height: '12.5px' })};
    }
  }
`;

EmailNewsletter.SubmitButton = styled(Button)`
  position: absolute;
  top: 45px;
  right: 8px;
  width: fit-content;
  height: fit-content;
  ${variableFontRegular({ size: '11px', height: '14px' })};
  background-color: ${({ theme }) => theme.navigation.textColor};
  color: ${({ theme }) => theme.navigation.backgroundColor};
  border: 1px solid ${({ theme }) => theme.navigation.textColor};
  text-transform: none;
  padding: 7px 16px;
  border-radius: ${({ theme }) => theme.borderRadii.button};

  :active {
    border: 1px solid ${({ theme }) => theme.navigation.textColor};
  }

  @media (hover: hover) {
    &:hover {
      text-decoration: underline;
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    padding: 7px 18px;
    top: 36px;
  }
`;

EmailNewsletter.Disclaimer = styled.div<{ isVisible: boolean }>`
  color: ${({ theme }) => theme.colors.darkGray};
  ${variableFontRegular({ size: '9.5px' })};
  opacity: 0;
  visibility: hidden;
  height: ${({ isVisible }) => (isVisible ? '100%' : 0)};
  ${({ isVisible }) => isVisible && toggleVisible};
  margin-top: 16px;

  > a {
    text-decoration: underline;

    &:hover {
      text-decoration: underline;
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    height: 100%;
  }
`;
